import Box from "@/common/components/Box";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import ListHeader from "@/common/components/ListHeader";
import { BadgeInfoIcon, BellIcon, CircleChevronDown } from "lucide-react";
import { Tooltip } from "react-tooltip";
import NotificationsConfigurationsUpdateModal from "../Update";
import { useNotificationsConfigurationsAvailableNotifications } from "@/features/notifications/hooks/useNotificationConfigurationsAvailableNotifications";
import NotificationsConfigurationsLoadingState from "@/features/notifications/components/NotificationsConfigurationsLoadingState";
import { EmptyDataMessage } from "@/common/components/EmptyDataMessage";
import { getNotificationProviderOptionFromPossibleProviders } from "@/features/notifications/constants/providers.constant";
import { NotificationConfigurationsData } from "@/features/notifications/types/configurations.type";
import { useNotificationsConfigurations } from "@/features/notifications/hooks/useNotificationConfigurations";
import { Mapper } from "@/common/services/mapper";
import { NotificationsConfigurationsTransformer } from "@/features/notifications/services/notificationsConfigurationsTransformer";

const NotificationsConfigurationsShow = () => {
  const {
    data: availableNotifications,
    isPending: isAvailableNotificationsPending,
    refetch: refetchRequest,
  } = useNotificationsConfigurationsAvailableNotifications();

  const {
    data: notificationsConfigurations,
    isPending: isNotificationsConfigurationsPending,
    error: requestError = {} as any,
  } = useNotificationsConfigurations<Record<string, NotificationConfigurationsData>>({
    mapper: Mapper.mapWithTransformer({ transformer: NotificationsConfigurationsTransformer.strategyTransformer() }),
  });

  return (
    <div className="flex flex-col gap-6">
      <ListHeader
        Icon={<BellIcon className="w-10 h-10" strokeWidth={1.5} />}
        title="Notificações"
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={requestError?.status}
        refetchFunction={refetchRequest}
      >
        <Box className="h-[calc(100vh-128px)] overflow-y-auto flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <p className="text-xl">
              Configurações
            </p>

            {availableNotifications && (
              <NotificationsConfigurationsUpdateModal
                availableNotifications={availableNotifications}
                configurations={notificationsConfigurations}
              />
            )}
          </div>

          {(isAvailableNotificationsPending || isNotificationsConfigurationsPending) && <NotificationsConfigurationsLoadingState />}

          {!isAvailableNotificationsPending && !availableNotifications && (
            <EmptyDataMessage />
          )}

          {!isAvailableNotificationsPending && availableNotifications && notificationsConfigurations && (
            <div className="flex flex-col gap-4">
              {availableNotifications.map((notification) => {
                const config = notificationsConfigurations[notification.notification_klass];
                const providers = getNotificationProviderOptionFromPossibleProviders(notification.possible_providers);

                return (
                  <div key={notification.notification_klass} className="flex items-center gap-2">
                    <p className="text-sm text-white-800">
                      {notification.display_name}:
                    </p>

                    <BadgeInfoIcon
                      strokeWidth={1.5}
                      color="var(--warning)"
                      className="size-5"
                      data-tooltip-id={`notification-setting-${notification.notification_klass}-tooltip`}
                      data-tooltip-place="top-start"
                    />

                    <Tooltip
                      id={`notification-setting-${notification.notification_klass}-tooltip`}
                      className="max-w-[234px] opacity-100 backdrop-blur-3xl"
                    >
                      {notification.description}
                    </Tooltip>

                    <div className="flex items-center gap-1">
                      <CircleChevronDown strokeWidth={1.5} className="size-5" />

                      <p className="text-sm text-white-400">
                        {Number(config?.provider_klasses?.length || 0) === 0
                          ? 'Todos'
                          : config?.provider_klasses?.map((provider) => providers[provider as never]).join(', ')}
                      </p>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </Box>
      </ErrorMessageWrapper>
    </div>
  );
}

export default NotificationsConfigurationsShow;
